import React, { useEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { useExitAnim } from "../../hooks/useExitAnim";
import {
  useDMSamples,
  SampleType,
  DMSampleData,
  FollowUpSampleData,
  SampleMessage,
} from "../../hooks/useDMSamples";
import ChatBubble, { MiniBubble } from "./ChatBubble";
import type { MiniBubbleMessage } from "./ChatBubble";
import { CopyConversationButton } from "./CopyConversationButton";
import { serializeConversation } from "../../utils/conversationToText";
import { formatDateTimeInTZ } from "../../lib/timezone";

// =============================================================================
// DM SAMPLE MODAL
// =============================================================================
// PURPOSE: DM conversation viewer for metric drill-downs. Non-follow-up metrics
// render as a 3-column grid of card-style chat threads (matches ProofGridView
// visual pattern). Follow-up metric renders as a sequence card list.
//
// Data from useDMSamples (get_dm_samples RPC) — messages are embedded in the
// response, no per-card fetch needed (unlike ProofGridView).
// =============================================================================

// Convert SampleMessage (RPC shape) → MiniBubbleMessage (DB shape) for MiniBubble.
const toMini = (msg: SampleMessage): MiniBubbleMessage => ({
  message_text: msg.text ?? null,
  sent_at: msg.sent_at,
  sender_type: msg.sender,
  has_media: msg.has_media,
});

// Grid card — one conversation with inline chat bubbles
const GridCard: React.FC<{
  contactName: string;
  leadInstagram: string | null;
  messages: SampleMessage[];
  timezone: string;
  msgCount: number;
}> = ({ contactName, leadInstagram, messages, timezone, msgCount }) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const safeMessages = messages || [];

  // Scroll to the last message after render — matches ProofGridView behavior for
  // metrics without explicit highlights (no "special" message to center on).
  useEffect(() => {
    if (!scrollRef.current || safeMessages.length === 0) return;
    const timer = setTimeout(() => {
      if (!scrollRef.current) return;
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }, 350);
    return () => clearTimeout(timer);
  }, [safeMessages.length]);

  return (
    <div
      className="border border-black/[0.08] rounded-lg overflow-hidden liquid-glass anim-lift"
      data-testid="dm-sample-grid-card"
    >
      {/* Header */}
      <div className="flex items-center gap-2 px-3 py-2 bg-black/[0.03] border-b border-black/[0.08]">
        <div className="flex-1 min-w-0">
          <span className="token-11 font-medium text-glass truncate block">
            {contactName || "Unknown"}
          </span>
          {leadInstagram && (
            <span className="token-10 text-glass-muted truncate block">@{leadInstagram}</span>
          )}
        </div>
        <span className="token-10 text-glass-secondary shrink-0 bg-black/[0.05] px-1.5 py-0.5 rounded">
          {msgCount} msgs
        </span>
        <div className="shrink-0" onClick={(e) => e.stopPropagation()}>
          <CopyConversationButton
            variant="card"
            disabled={!safeMessages.length}
            getText={() =>
              serializeConversation({
                messages: safeMessages,
                contactName,
                leadInstagram,
                timezone,
              })
            }
          />
        </div>
      </div>

      {/* Messages */}
      <div
        ref={scrollRef}
        className="max-h-80 overflow-y-auto p-2 bg-black/[0.03]"
        style={{ overscrollBehavior: "contain" }}
      >
        {safeMessages.length === 0 ? (
          <p className="token-10 text-glass-muted text-center py-4">(no messages)</p>
        ) : (
          safeMessages.map((msg, i) => (
            <MiniBubble key={i} message={toMini(msg)} timezone={timezone} />
          ))
        )}
      </div>
    </div>
  );
};

// Follow-up sequence view — kept as-is since follow-ups are conceptually
// different (first-outreach + follow-up pair with days-between), not a
// conversation grid.
const FollowUpCard: React.FC<{
  contactName: string;
  leadInstagram: string | null;
  firstOutreach: string;
  firstOutreachAt: string;
  followupMessage: string;
  followupAt: string;
  daysBetween: number;
  allMessages: SampleMessage[];
  timezone: string;
}> = ({
  contactName,
  leadInstagram,
  firstOutreach,
  firstOutreachAt,
  followupMessage,
  followupAt,
  daysBetween,
  allMessages,
  timezone,
}) => {
  const [showFull, setShowFull] = React.useState(false);

  return (
    <div className="border border-black/[0.08] rounded-lg mb-4 overflow-hidden">
      <div className="flex items-center justify-between p-3 bg-black/[0.03]">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-pink-500 via-red-500 to-yellow-500 flex items-center justify-center text-glass font-bold">
            {contactName.charAt(0).toUpperCase()}
          </div>
          <div>
            <p className="font-medium text-glass">{contactName}</p>
            {leadInstagram && <p className="token-11 text-glass-muted">@{leadInstagram}</p>}
          </div>
        </div>
        <span className="px-2 py-1 bg-pink-500/15 text-pink-400 token-11 font-medium rounded">
          {daysBetween} day{daysBetween !== 1 ? "s" : ""} apart
        </span>
      </div>

      <div className="p-3 bg-black/[0.03]">
        <div className="flex justify-end mb-2">
          <div className="max-w-[80%] px-3 py-2 rounded-2xl bg-blue-400 text-glass rounded-br-none">
            <p className="token-11 text-blue-100 mb-1">First Outreach</p>
            <p className="token-14">{firstOutreach}</p>
            <p className="token-10 text-blue-100 mt-1">
              {formatDateTimeInTZ(firstOutreachAt, timezone)}
            </p>
          </div>
        </div>

        <div className="flex justify-center my-2">
          <span className="token-11 text-glass-muted">
            ({daysBetween} day{daysBetween !== 1 ? "s" : ""} later)
          </span>
        </div>

        <div className="flex justify-end mb-2">
          <div className="max-w-[80%] px-3 py-2 rounded-2xl bg-blue-500 text-glass rounded-br-none">
            <p className="token-11 text-blue-100 mb-1">Follow-up</p>
            <p className="token-14">{followupMessage}</p>
            <p className="token-10 text-blue-100 mt-1">
              {formatDateTimeInTZ(followupAt, timezone)}
            </p>
          </div>
        </div>

        {allMessages.length > 2 && (
          <button
            onClick={() => setShowFull(!showFull)}
            className="token-11 text-ds-accent hover:underline mt-2"
          >
            {showFull ? "Hide full conversation" : `Show all ${allMessages.length} messages`}
          </button>
        )}

        {showFull && (
          <div className="mt-3 pt-3 border-t border-black/[0.08]">
            {allMessages.map((msg, i) => (
              <ChatBubble key={i} message={msg} timezone={timezone} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

interface DMSampleModalProps {
  isOpen: boolean;
  onClose: () => void;
  sampleType: SampleType;
  title: string;
  organizationId: string | null;
  dateRange: { start: Date | null; end: Date | null };
  timezone?: string;
}

const DMSampleModal: React.FC<DMSampleModalProps> = ({
  isOpen,
  onClose,
  sampleType,
  title,
  organizationId,
  dateRange,
  timezone = "UTC",
}) => {
  const { data, loading, error, fetchSamples, loadMore, reset } = useDMSamples(
    organizationId,
    dateRange
  );
  const scrollRef = useRef<HTMLDivElement>(null);
  const modalRef = useRef<HTMLDivElement>(null);
  // Keep the modal mounted through the 200ms exit so anim-modal-out plays.
  const { mounted, closing } = useExitAnim(isOpen, 200);

  useEffect(() => {
    if (isOpen && organizationId) {
      fetchSamples(sampleType, 12);
    }
    return () => {
      if (!isOpen) reset();
    };
  }, [isOpen, sampleType, organizationId, fetchSamples, reset]);

  useEffect(() => {
    if (!isOpen) return;
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [isOpen, onClose]);

  useEffect(() => {
    if (!isOpen || !modalRef.current) return;
    const modal = modalRef.current;
    const focusable = modal.querySelectorAll<HTMLElement>(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    const handleTab = (e: KeyboardEvent) => {
      if (e.key !== "Tab") return;
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last?.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first?.focus();
      }
    };

    modal.addEventListener("keydown", handleTab);
    first?.focus();
    return () => modal.removeEventListener("keydown", handleTab);
  }, [isOpen]);

  const handleScroll = useCallback(() => {
    if (!scrollRef.current || loading || !data) return;
    const { scrollTop, scrollHeight, clientHeight } = scrollRef.current;
    if (scrollTop + clientHeight >= scrollHeight - 200) {
      const currentCount =
        sampleType === "follow_up"
          ? (data as FollowUpSampleData).conversations?.length || 0
          : (data as DMSampleData).conversations?.length || 0;
      const totalCount = data.total_count;
      if (currentCount < totalCount) {
        loadMore(sampleType, currentCount);
      }
    }
  }, [loading, data, sampleType, loadMore]);

  if (!mounted) return null;

  const isFollowUp = sampleType === "follow_up";
  const dmData = data as DMSampleData | null;
  const followUpData = data as FollowUpSampleData | null;

  const currentCount = isFollowUp
    ? followUpData?.conversations?.length || 0
    : dmData?.conversations?.length || 0;
  const totalCount = data?.total_count || 0;
  const hasMore = currentCount < totalCount;

  const typeStyles: Record<
    SampleType,
    { bgColor: string; textColor: string; borderColor: string }
  > = {
    new_dm: {
      bgColor: "bg-ds-accent/10",
      textColor: "text-ds-accent",
      borderColor: "border-ds-accent/30",
    },
    inbound_new_dm: {
      bgColor: "bg-ds-accent/10",
      textColor: "text-ds-accent",
      borderColor: "border-ds-accent/30",
    },
    manychat: {
      bgColor: "bg-ds-amber-bg",
      textColor: "text-ds-amber",
      borderColor: "border-ds-amber/30",
    },
    manual_outbound: {
      bgColor: "bg-ds-amber-bg",
      textColor: "text-ds-amber",
      borderColor: "border-ds-amber/30",
    },
    follow_up: {
      bgColor: "bg-pink-500/10",
      textColor: "text-pink-400",
      borderColor: "border-pink-400/30",
    },
    engaged: {
      bgColor: "bg-ds-accent/10",
      textColor: "text-ds-accent",
      borderColor: "border-ds-accent/30",
    },
    real_convo: {
      bgColor: "bg-ds-purple/10",
      textColor: "text-ds-purple",
      borderColor: "border-ds-purple/30",
    },
    ghosted: {
      bgColor: "bg-ds-red/10",
      textColor: "text-ds-red",
      borderColor: "border-ds-red/30",
    },
  };

  const { bgColor, textColor, borderColor } = typeStyles[sampleType];

  return createPortal(
    <div
      className="fixed inset-0 z-[9999] overflow-y-auto"
      style={{ overscrollBehavior: "contain" }}
    >
      <div
        className={`${closing ? "anim-backdrop-out" : "anim-backdrop-in"} fixed inset-0 bg-black/50 backdrop-blur-sm transition-opacity`}
        onClick={onClose}
      />

      {/* anim-modal-in/out on the NON-glass wrapper (glass panel can't take transforms).
          onClick closes on a click on the wrapper itself (empty area), not the card —
          this wrapper covers the backdrop, so it must own the outside-click-to-close. */}
      <div
        className={`${closing ? "anim-modal-out" : "anim-modal-in"} flex min-h-full items-center justify-center p-4`}
        onClick={(e) => {
          if (e.target === e.currentTarget) onClose();
        }}
      >
        <div
          ref={modalRef}
          role="dialog"
          aria-modal="true"
          aria-labelledby="dm-sample-modal-title"
          className={`relative w-full ${isFollowUp ? "max-w-2xl" : "max-w-[85vw]"} max-h-[85vh] detail-overlay rounded-xl shadow-2xl flex flex-col`}
          data-testid="dm-sample-modal"
        >
          {/* Header */}
          <div className={`flex items-center justify-between p-4 border-b ${borderColor} shrink-0`}>
            <div className="flex items-center gap-3">
              <span
                id="dm-sample-modal-title"
                className={`px-3 py-1 ${bgColor} ${textColor} token-14 font-medium rounded-full`}
              >
                {title}
              </span>
              <span className="token-14 text-glass-muted">{totalCount.toLocaleString()} total</span>
            </div>
            <button
              onClick={onClose}
              className="anim-tap p-2 hover:bg-black/[0.05] rounded-full transition-colors"
              aria-label="Close modal"
            >
              <svg
                className="w-5 h-5 text-glass-muted"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          {/* Content */}
          <div
            ref={scrollRef}
            onScroll={handleScroll}
            className="p-6 overflow-y-auto flex-1"
            style={{ overscrollBehavior: "contain" }}
          >
            {error && (
              <div className="p-4 bg-ds-red/10 border border-ds-red/30 text-ds-red rounded-lg mb-4">
                Error: {error}
              </div>
            )}

            {loading && !data && (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
                <span className="ml-3 text-glass-muted">Loading conversations...</span>
              </div>
            )}

            {/* Non-follow-up: 3-column grid */}
            {!isFollowUp && dmData?.conversations && (
              <>
                <p className="token-11 text-glass-muted mb-3">
                  {currentCount} of {totalCount.toLocaleString()} conversations
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                  {dmData.conversations.map((conv, i) => (
                    <div
                      key={conv.conversation_id || i}
                      className="animate-[fadeSlideIn_0.3s_ease-out_forwards]"
                      style={{ animationDelay: `${(i % 9) * 30}ms`, opacity: 0 }}
                    >
                      <GridCard
                        contactName={conv.contact_name}
                        leadInstagram={conv.lead_instagram}
                        messages={conv.messages}
                        timezone={timezone}
                        msgCount={conv.messages?.length || 0}
                      />
                    </div>
                  ))}
                </div>
              </>
            )}

            {/* Follow-ups: sequence cards */}
            {isFollowUp && followUpData?.conversations && (
              <div>
                <p className="token-11 text-glass-muted mb-3">
                  {currentCount} of {totalCount.toLocaleString()} follow-up sequences
                </p>
                {followUpData.conversations.map((seq, i) => (
                  <FollowUpCard
                    key={i}
                    contactName={seq.contact_name}
                    leadInstagram={seq.lead_instagram}
                    firstOutreach={seq.first_outreach}
                    firstOutreachAt={seq.first_outreach_at}
                    followupMessage={seq.followup_message}
                    followupAt={seq.followup_at}
                    daysBetween={seq.days_between}
                    allMessages={seq.all_messages}
                    timezone={timezone}
                  />
                ))}
              </div>
            )}

            {/* Load more / end / empty */}
            {loading && data && (
              <div className="flex justify-center py-4 mt-4">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500" />
              </div>
            )}

            {!loading && currentCount >= totalCount && currentCount > 0 && (
              <p className="text-center token-14 text-glass-muted py-4 mt-4">End of results</p>
            )}

            {!loading && data && currentCount === 0 && (
              <p className="text-center text-glass-secondary py-12">
                No conversations found for this filter.
              </p>
            )}
          </div>

          {/* Footer */}
          {hasMore && (
            <div className="p-3 border-t border-black/[0.08] text-center shrink-0">
              <p className="token-11 text-glass-muted">Scroll down to load more conversations</p>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default DMSampleModal;
